import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, Button, Checkbox, Dialog, InputNumber, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";

export const schema = yup.object().shape({
  show_only_billable: yup.boolean(),
  low: yup.number().required().max(100).min(1),
  high: yup
    .number()
    .required()
    .max(100)
    .min(1)
    .test("is-greater-than-low", "High must be greater than Low", function (value) {
      const { low } = this.parent;
      if (low > 100 || value > 100 || low === undefined || value === undefined) {
        return true;
      }
      return value > low;
    }),
  critical: yup
    .number()
    .required()
    .max(100)
    .min(1)
    .test("is-greater-than-high", "Critical must be greater than High", function (value) {
      const { high } = this.parent;
      if (high > 100 || value > 100 || high === undefined || value === undefined) {
        return true;
      }
      return value > high;
    }),
});

export const UtilizationDialog = ({ open, onClose }) => {
  const { t } = useTranslation();

  const inputData = useMemo(
    () => [
      { name: "low", label: t("or bellow is considered low utilization.") },
      {
        name: "high",
        label: t("or above is considered high utilization."),
      },
      {
        name: "critical",
        label: t("or above is considered critical utilization."),
      },
    ],
    [t]
  );
  const dispatch = useDispatch();

  const showOnlyBillable = useSelector((state) =>
    getRawPersonalizedConfigOption(state, "utilization_show_only_billable_hours")
  );
  const utilizationLevels = useSelector((state) => getRawPersonalizedConfigOption(state, "utilization_levels"));

  const handleSubmit = useCallback(
    (values) => {
      const { utilization_show_only_billable_hours, ...rest } = values;
      dispatch(
        editPersonalizedConfigOptions({
          utilization_show_only_billable_hours,
          utilization_levels: rest,
        })
      ).then(() => onClose());
    },
    [dispatch, onClose]
  );

  return (
    <Dialog
      enableBackgroundClick
      open={open}
      onClose={onClose}
    >
      <Dialog.Title>{t("Setup Utilization Report")}</Dialog.Title>
      <Dialog.ContentDivider />
      <Formik
        validateOnMount={true}
        onSubmit={handleSubmit}
        validationSchema={schema}
        initialValues={{
          utilization_show_only_billable_hours: showOnlyBillable,
          low: utilizationLevels?.get("low"),
          high: utilizationLevels?.get("high"),
          critical: utilizationLevels?.get("critical"),
        }}
        initialTouched={{
          low: true,
          high: true,
          critical: true,
        }}
      >
        {({ isValid }) => (
          <Form>
            <Dialog.Content className="tw-flex tw-flex-col tw-gap-4">
              <Body2>
                {t(
                  "We calculate utilization using billable and non-billable hours. This can be adjusted to include billable time only."
                )}
              </Body2>
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <Field name="utilization_show_only_billable_hours">
                  {({ field, form }) => (
                    <Checkbox
                      id="utilization_show_only_billable_hours"
                      checked={Boolean(field.value)}
                      onChange={(e) => {
                        form.setFieldValue("utilization_show_only_billable_hours", e.target.checked);
                      }}
                    />
                  )}
                </Field>
                <Label
                  weight="regular"
                  className="tw-cursor-pointer"
                  htmlFor="utilization_show_only_billable_hours"
                >
                  {t("Use billable time only")}
                </Label>
              </div>
              <hr className="tw-w-full tw-border-0 tw-m-0 tw-h-[1px] tw-bg-border-primary" />
              <Body2>
                {t(
                  "The system uses default thresholds for utilization levels. Those can be adjusted to match your needs."
                )}
              </Body2>
              <div className="tw-flex tw-flex-col tw-gap-3">
                {inputData.map((inputItem) => (
                  <div
                    key={inputItem.name}
                    className="tw-flex tw-items-center tw-gap-0.5"
                  >
                    <Field name={inputItem.name}>
                      {({ field, form, meta }) => (
                        <InputNumber
                          id={inputItem.name}
                          name={field.name}
                          className="tw-w-10 tw-px-0"
                          align="center"
                          value={field.value}
                          onChange={(value) => {
                            form.setFieldTouched(field.name, true);
                            form.setFieldValue(field.name, value);
                          }}
                          decimalLength={0}
                          invalid={meta.touched && meta.error}
                          min={1}
                        />
                      )}
                    </Field>
                    <Label
                      weight="regular"
                      htmlFor={inputItem.name}
                    >
                      % {inputItem.label}
                    </Label>
                  </div>
                ))}
              </div>
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                type="submit"
                variant="primary"
                className="tw-mr-3"
                disabled={!isValid}
              >
                {t("Save")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

UtilizationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
